import type { MemberRole } from './member';

/**
 * Permission levels for various actions.
 * Maps to backend access.Permission constants.
 *
 * IMPORTANT: These checks are for UI convenience only.
 * The backend MUST enforce all permissions.
 */
export type Permission =
  | 'view_analytics'
  | 'export_data'
  | 'manage_sources'
  | 'edit_project'
  | 'delete_project'
  | 'manage_projects' // Alias for creating projects
  | 'invite_members'
  | 'manage_members' // Alias for invite + role management
  | 'manage_roles'
  | 'view_billing'
  | 'manage_billing'
  | 'manage_digests'
  | 'access_api'
  | 'use_chat';

/**
 * Role-based permission matrix.
 * Mirrors backend member.Role permissions.
 */
export const ROLE_PERMISSIONS: Record<MemberRole, Permission[]> = {
  owner: [
    'view_analytics',
    'export_data',
    'manage_sources',
    'edit_project',
    'delete_project',
    'manage_projects',
    'invite_members',
    'manage_members',
    'manage_roles',
    'view_billing',
    'manage_billing',
    'manage_digests',
    'access_api',
    'use_chat',
  ],
  admin: [
    'view_analytics',
    'export_data',
    'manage_sources',
    'edit_project',
    'delete_project',
    'manage_projects',
    'invite_members',
    'manage_members',
    'manage_roles',
    'manage_digests',
    'access_api',
    'use_chat',
  ],
  analyst: ['view_analytics', 'export_data', 'manage_digests', 'access_api', 'use_chat'],
  viewer: ['view_analytics'],
};

/**
 * Check if role has specific permission.
 */
export const hasPermission = (role: MemberRole, permission: Permission): boolean => {
  return ROLE_PERMISSIONS[role]?.includes(permission) ?? false;
};

/**
 * Check if role has any of the specified permissions.
 */
export const hasAnyPermission = (role: MemberRole, permissions: Permission[]): boolean => {
  return permissions.some((p) => hasPermission(role, p));
};

/**
 * Check if role has all of the specified permissions.
 */
export const hasAllPermissions = (role: MemberRole, permissions: Permission[]): boolean => {
  return permissions.every((p) => hasPermission(role, p));
};
